/**
 * bridgeSound — Wave-3 Item 3: bridge event sound (default OFF).
 *
 * CONSUMERS:
 * - frontend/src/pages/FleetHud.tsx: diffs successive poll snapshots and plays
 *   one tone per returned event when the pref is on.
 * - frontend/src/components/bridge/BridgeTopBar.tsx: the '♪' toggle that owns
 *   the persisted pref.
 *
 * Three exports, three concerns:
 *  (a) soundEvents(prev, next) — PURE diff of two poll snapshots. Operates
 *      only on the inputs it receives; never fetches, never synthesizes
 *      snapshot fields. Returns [] on no-change.
 *  (b) playSoundEvent(event) — WebAudio player with NO audio assets: tones
 *      are synthesized from oscillators. The AudioContext is created LAZILY
 *      on the first play call while the pref is enabled (autoplay policy) and
 *      is a singleton for the page lifetime — module import never constructs
 *      one, and a poll with the pref OFF never touches WebAudio at all.
 *  (c) getSoundEnabled / setSoundEnabled — persisted pref at localStorage
 *      key 'fleet-sound-enabled', default 'false'.
 *
 * Snapshot contract (BridgePollSnapshot):
 *   items_summary — Record<itemId, status>. FleetHud encodes a commit that
 *   appears on an item as `${status}|${commit}` so the pure diff can see a
 *   commit landing without a status change; plain status strings are the
 *   canonical form.
 *   escalation_count — open escalation count from the poll.
 *   stage — the pipeline stage string the page already derives.
 *
 * Reduced motion: when `(prefers-reduced-motion: reduce)` matches, the gain
 * envelope is stepped discretely via setValueAtTime — no ramp-based fades.
 */
export interface BridgePollSnapshot {
  items_summary: Record<string, string>;
  escalation_count: number;
  stage: string;
}

export type SoundEvent = 'stage_pass' | 'merged' | 'escalation';

const TERMINAL_STATUSES = new Set(['verified', 'completed']);

/** Split a FleetHud-encoded value into [status, commit?]. */
function parseItemValue(value: string | undefined): [string, string | undefined] {
  if (!value) return ['', undefined];
  const idx = value.indexOf('|');
  if (idx === -1) return [value, undefined];
  return [value.slice(0, idx), value.slice(idx + 1)];
}

/**
 * Pure diff. Ordered, at most one event per type: stage_pass first, then
 * merged, then escalation. Identical snapshots → [].
 */
export function soundEvents(prev: BridgePollSnapshot, next: BridgePollSnapshot): SoundEvent[] {
  const events: SoundEvent[] = [];

  if (prev.stage !== next.stage) events.push('stage_pass');

  let merged = false;
  for (const key of Object.keys(next.items_summary)) {
    const nv = next.items_summary[key];
    const pv = prev.items_summary[key];
    if (nv === pv) continue;
    const [nStatus, nCommit] = parseItemValue(nv);
    const [pStatus, pCommit] = parseItemValue(pv);
    // Item gained a terminal status, or a commit appeared on it.
    if ((TERMINAL_STATUSES.has(nStatus) && nStatus !== pStatus) || (nCommit && nCommit !== pCommit)) {
      merged = true;
      break;
    }
  }
  if (merged) events.push('merged');

  if (next.escalation_count > prev.escalation_count) events.push('escalation');

  return events;
}

// ---------------------------------------------------------------------------
// Persisted pref
// ---------------------------------------------------------------------------

const PREF_KEY = 'fleet-sound-enabled';

export function getSoundEnabled(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(PREF_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setSoundEnabled(v: boolean): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(PREF_KEY, v ? 'true' : 'false');
  } catch {
    // storage unavailable (private mode etc.) — the pref just won't persist.
  }
}

// ---------------------------------------------------------------------------
// WebAudio player — lazy singleton, synthesized tones, no assets.
// ---------------------------------------------------------------------------

let audioCtx: AudioContext | null = null;

/** Lazily create THE one AudioContext for the page; null when unsupported. */
function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) audioCtx = new Ctor();
  // Autoplay policy may leave it suspended; resume is a no-op when running.
  if (audioCtx.state === 'suspended') void audioCtx.resume();
  return audioCtx;
}

interface ToneStep {
  freq: number;
  type: OscillatorType;
  /** seconds from tone start */
  offset: number;
  /** seconds */
  duration: number;
  gain: number;
}

const TONES: Record<SoundEvent, ToneStep[]> = {
  stage_pass: [{ freq: 880, type: 'sine', offset: 0, duration: 0.12, gain: 0.08 }],
  merged: [
    { freq: 660, type: 'sine', offset: 0, duration: 0.15, gain: 0.08 },
    { freq: 990, type: 'sine', offset: 0.15, duration: 0.15, gain: 0.08 },
  ],
  escalation: [{ freq: 220, type: 'square', offset: 0, duration: 0.4, gain: 0.03 }],
};

/**
 * Play one event's tone. Guarded by the pref — the player path is entered
 * only after the user-enabled toggle; with the pref OFF this returns before
 * any AudioContext construction.
 */
export function playSoundEvent(event: SoundEvent): void {
  if (!getSoundEnabled()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const reduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const t0 = ctx.currentTime;
  for (const step of TONES[event]) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = step.type;
    osc.frequency.setValueAtTime(step.freq, t0 + step.offset);
    if (reduced) {
      // Discrete envelope steps only — no ramp-based fade loops.
      gain.gain.setValueAtTime(step.gain, t0 + step.offset);
      gain.gain.setValueAtTime(0, t0 + step.offset + step.duration);
    } else {
      gain.gain.setValueAtTime(step.gain, t0 + step.offset);
      gain.gain.linearRampToValueAtTime(0, t0 + step.offset + step.duration);
    }
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0 + step.offset);
    osc.stop(t0 + step.offset + step.duration);
  }
}
